
import React, { createContext, useContext, useEffect } from "react";
import { useThemeUtils } from "@/hooks/useThemeUtils";

type Theme = "dark" | "light";

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({
  children,
  defaultTheme = "light",
}: ThemeProviderProps) {
  // Keep light/dark theme in localStorage (this remains per-user)
  const [theme, setTheme] = React.useState<Theme>(
    () => (localStorage.getItem("theme") as Theme) || defaultTheme
  );
  
  // Only use global theme utilities, NOT organization theme
  // Organization theme is now exclusively managed by OrganizationProvider via useThemeApplicator
  const { 
    globalPrimaryColor: primaryColor, 
    setGlobalTheme: setPrimaryColor, 
    globalThemeLoading: loading 
  } = useThemeUtils();

  // Apply light/dark mode class to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const value = {
    theme,
    setTheme,
    primaryColor,
    setPrimaryColor,
    loading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
